import React, { forwardRef, useRef, useState, } from "react";
import { usePopper } from "react-popper";
import FocusTrap from "focus-trap-react";
import ReactDOM from "react-dom";
import { THEME } from "./theming/theming";
import { Flex } from "./Flex/Flex";
import { View } from "./View/View";
import { Button } from "./Button";
import { UtilityStyle } from "./hooks/useUtilityStyles";
export var OverlayTriggerType;
(function (OverlayTriggerType) {
    OverlayTriggerType["Hover"] = "hover";
    OverlayTriggerType["Click"] = "click";
    OverlayTriggerType["None"] = "none";
})(OverlayTriggerType || (OverlayTriggerType = {}));
export var OverlayStyle;
(function (OverlayStyle) {
    OverlayStyle["Popover"] = "popover";
    OverlayStyle["Dropdown"] = "dropdown";
    OverlayStyle["Popout"] = "popout";
})(OverlayStyle || (OverlayStyle = {}));
export const OverlayTrigger = ({ ariaLabel, container, displayArrow, overlayContent, overlayTitle, overlayTrigger, placement = "bottom", strategy = "fixed", triggerType = OverlayTriggerType.Click, overlayStyle, onOutsideClickDeactivate, dataTestId, size, onClose, closeButton, open, targetRef, }) => {
    const [triggerElement, setTriggerElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const [show, setShow] = useState(false);
    const [focused, setFocused] = useState(false);
    const options = { modifiers: [], placement, strategy };
    const triggerProps = {};
    const isControlled = triggerType === OverlayTriggerType.None && open !== undefined;
    const isArrowDisplayed = displayArrow ||
        (displayArrow === undefined && overlayStyle === OverlayStyle.Popover);
    if (isArrowDisplayed) {
        options.modifiers.push({
            name: "arrow",
            options: { element: arrowElement },
        });
        options.modifiers.push({ name: "offset", options: { offset: [0, 11] } });
    }
    const { styles, attributes } = usePopper(triggerElement || targetRef?.current, popperElement, options);
    const handleClick = () => {
        setShow(!show);
    };
    const handleMouseEnter = () => {
        setShow(true);
    };
    const handleMouseLeave = () => {
        setShow(false);
    };
    const handleFocus = () => {
        setShow(true);
    };
    const handleBlur = () => {
        if (show && !focused) {
            handleOnClose();
        }
    };
    const handleOnClose = () => {
        setShow(false);
        onClose && onClose();
    };
    if (triggerType === OverlayTriggerType.Click) {
        triggerProps.onClick = handleClick;
    }
    else if (triggerType === OverlayTriggerType.Hover) {
        triggerProps.onMouseEnter = handleMouseEnter;
        triggerProps.onFocus = handleFocus;
        triggerProps.onMouseLeave = handleMouseLeave;
        triggerProps.onBlur = handleBlur;
    }
    const overlay = (React.createElement(Overlay, { overlayContent: overlayContent, overlayTitle: overlayTitle, overlayStyle: overlayStyle, styles: styles, attributes: attributes.popper, popperRef: setPopperElement, arrowRef: setArrowElement, isArrowDisplayed: isArrowDisplayed, closePopover: () => handleOnClose(), setFocused: (focus) => setFocused(focus), onOutsideClickDeactivate: onOutsideClickDeactivate, dataTestId: dataTestId, ariaLabel: ariaLabel, triggerEl: triggerElement, size: size, closeButton: closeButton }));
    const shouldShow = isControlled ? open : show;
    return (React.createElement(React.Fragment, null,
        targetRef ? (overlayTrigger) : (React.createElement("span", { className: "popper-trigger", ref: setTriggerElement, ...triggerProps }, overlayTrigger)),
        shouldShow && !container?.current && overlay,
        shouldShow &&
            container?.current &&
            ReactDOM.createPortal(overlay, container.current)));
};
const Overlay = (props) => {
    const { closePopover, onOutsideClickDeactivate, setFocused, triggerEl } = props;
    const contentRef = useRef(null);
    const handleClose = () => {
        setFocused(false);
        closePopover();
        if (onOutsideClickDeactivate) {
            onOutsideClickDeactivate();
        }
    };
    const handleClick = (event) => {
        if (!props?.closeButton && event.target !== contentRef.current)
            closePopover();
    };
    const handleKeyPress = (event) => {
        if (event.which === 32) {
            handleClose();
        }
    };
    const onClickOutside = (event) => {
        if (triggerEl && event.target && triggerEl.contains(event.target)) {
            return true;
        }
        handleClose();
        return true;
    };
    return (React.createElement(FocusTrap, { focusTrapOptions: {
            onActivate: () => setFocused(true),
            clickOutsideDeactivates: onClickOutside,
            escapeDeactivates: () => {
                handleClose();
                return true;
            },
            initialFocus: false,
            returnFocusOnDeactivate: false,
            fallbackFocus: ".popper-overlay",
        } },
        React.createElement(OverlayTriggerContent, { ...props, ref: contentRef, onClick: handleClick, onKeyPress: handleKeyPress, onClose: () => closePopover() })));
};
export const OverlayTriggerContent = forwardRef((props, contentRef) => (React.createElement("div", { "aria-label": props?.ariaLabel, "aria-hidden": true, ref: props?.popperRef, onClick: props?.onClick, onKeyPress: props?.onKeyPress, style: props?.size
        ? {
            ...props?.styles.popper,
            maxWidth: getSize(props?.size),
            width: getSize(props?.size),
        }
        : props?.styles.popper, ...props?.attributes, className: `popper-overlay popper-${props?.overlayStyle}`, tabIndex: -1, "data-testid": props?.dataTestId },
    React.createElement(Flex, { direction: "column", ref: contentRef },
        props?.overlayTitle && (React.createElement(View, { border: true, borderWidthTop: "zero", borderWidthLeft: "zero", borderWidthRight: "zero" },
            React.createElement(Flex, null,
                React.createElement(View, { flex: 1, paddingTop: "medium", paddingBottom: "medium", paddingLeft: "large", paddingRight: "large" },
                    React.createElement("strong", null, props?.overlayTitle)),
                props?.closeButton && (React.createElement(View, { paddingRight: "large" },
                    React.createElement(Button, { label: "Close", onClick: props?.onClose, type: "suppressed", styleOptions: [
                            UtilityStyle.VerticalPaddingSmall,
                            UtilityStyle.TopSpaceSmall,
                        ] })))))),
        props?.overlayStyle !== OverlayStyle.Popout ? (React.createElement("div", { ref: contentRef, className: `popper-${props?.overlayStyle}-content` }, props?.overlayContent)) : (React.createElement(View, null, props?.overlayContent))),
    props?.isArrowDisplayed && props?.overlayStyle !== OverlayStyle.Popout && (React.createElement("div", { ref: props?.arrowRef, id: "popper-arrow", className: props?.overlayTitle ? "has-title" : "", "data-testid": "popper-arrow", style: props?.styles.arrow })))));
const getSize = (size) => {
    if (size === "large")
        return THEME.sizes.large;
    if (size === "medium")
        return THEME.sizes.medium;
    if (size === "smallplus")
        return THEME.sizes.smallplus;
    if (size === "small")
        return THEME.sizes.small;
    return THEME.sizes.small;
};
